import { Node, Position, internalsSymbol, HandleElement } from 'reactflow';

interface NodeWithInternals extends Node<any, string | undefined> {
  [internalsSymbol]: {
    handleBounds: {
      source: HandleElement[];
      target: HandleElement[];
    };
  };
}

/**
 * Type guard to check if a node has internals including handle bounds.
 * @param node - The node to check.
 * @returns True if the node has internals with handle bounds, otherwise false.
 */
function isNodeWithInternals(node: Node<any, string | undefined>): node is NodeWithInternals {
  return (
    node[internalsSymbol] !== undefined &&
    node[internalsSymbol].handleBounds !== undefined &&
    Array.isArray(node[internalsSymbol].handleBounds.source)
  );
}

/**
 * Calculates the center position of a node.
 * @param node - The node for which to calculate the center position.
 * @returns An object containing the x and y coordinates of the node's center.
 * @throws Will throw an error if the node's positionAbsolute, height, or width is not found.
 */
function getNodeCenter(node: Node<any, string | undefined>): { x: number; y: number } {
  if (!node.positionAbsolute) {
    throw new Error(`Position absolute not found for node: ${node}`);
  }
  if (!node.height) {
    throw new Error(`Node height not found for node: ${node}`);
  }
  if (!node.width) {
    throw new Error(`Node width not found for node: ${node}`);
  }
  return {
    x: node.positionAbsolute.x + node.width / 2,
    y: node.positionAbsolute.y + node.height / 2,
  };
}

/**
 * Gets the coordinates of a handle on a node based on its position.
 * @param node - The node containing the handle.
 * @param handlePosition - The position of the handle (Left, Right, Top, Bottom).
 * @returns A tuple containing the x and y coordinates of the handle.
 * @throws Will throw an error if the handle or node's positionAbsolute is not found.
 */
function getHandleCoordsByPosition(node: NodeWithInternals, handlePosition: Position): [number, number] {
  const handle = node[internalsSymbol].handleBounds.source.find(h => h.position === handlePosition);

  if (!handle) {
    throw new Error(`Handle not found for position: ${handlePosition}`);
  }

  if (!node.positionAbsolute) {
    throw new Error(`Position absolute not found for position: ${handlePosition}`);
  }

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
    default:
      offsetY = handle.width;
  }

  const x = node.positionAbsolute.x + handle.x + offsetX;
  const y = node.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
}

/**
 * Calculates the parameters needed to connect two nodes.
 * @param nodeA - The first node.
 * @param nodeB - The second node.
 * @returns A tuple containing the x and y coordinates of the handle on nodeA, and the position.
 * @throws Will throw an error if nodeA does not have internals.
 */
function getParams(
  nodeA: Node<any, string | undefined>,
  nodeB: Node<any, string | undefined>,
): [number, number, Position] {
  const centerA = getNodeCenter(nodeA);
  const centerB = getNodeCenter(nodeB);

  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);

  let position: Position;

  if (horizontalDiff > verticalDiff) {
    position = centerA.x > centerB.x ? Position.Left : Position.Right;
  } else {
    position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
  }

  if (!isNodeWithInternals(nodeA)) {
    throw new Error('NodeA does not have internals.');
  }

  const [x, y] = getHandleCoordsByPosition(nodeA, position);
  return [x, y, position];
}

/**
 * Calculates the parameters needed to create an edge between two nodes.
 * @param source - The source node.
 * @param target - The target node.
 * @returns An object containing the coordinates and positions for the source and target handles.
 */
export function getEdgeParams(
  source: Node<any, string | undefined>,
  target: Node<any, string | undefined>,
): { sx: number; sy: number; tx: number; ty: number; sourcePos: Position; targetPos: Position } {
  const [sx, sy, sourcePos] = getParams(source, target);
  const [tx, ty, targetPos] = getParams(target, source);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
}
